import {
  ANALYTICS_COOKIE,
  CONSENT_COOKIE,
  CONSENT_VERSION,
  consentCookie,
  cookieDomainsFor,
  parseConsent,
  readCookie,
  type ConsentRecord,
} from "./consent";

/**
 * The cookie choice in the browser: read it, save it, hear about changes, and
 * reopen the settings from a footer link. Everything is wrapped so a blocked
 * cookie jar or a missing document (prerender) never breaks a page.
 */

type Listener = (record: ConsentRecord | null) => void;

const listeners = new Set<Listener>();
const openListeners = new Set<() => void>();

function nowSeconds(): number {
  return Math.floor(Date.now() / 1000);
}

export function readConsent(): ConsentRecord | null {
  try {
    return parseConsent(readCookie(document.cookie, CONSENT_COOKIE), nowSeconds());
  } catch {
    return null;
  }
}

export function saveConsent(analytics: boolean): ConsentRecord {
  const record: ConsentRecord = { version: CONSENT_VERSION, analytics, decidedAt: nowSeconds() };
  try {
    document.cookie = consentCookie(
      record,
      window.location.hostname,
      window.location.protocol === "https:"
    );
  } catch {
    // Cookies blocked: the choice holds for this page view only.
  }
  for (const listener of listeners) listener(record);
  return record;
}

/** Called with every new choice. Returns the unsubscribe function. */
export function onConsentChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** "Nastavení cookies" in a footer or menu: shows the settings dialog. */
export function openCookieSettings(): void {
  for (const listener of openListeners) listener();
}

export function onOpenCookieSettings(listener: () => void): () => void {
  openListeners.add(listener);
  return () => openListeners.delete(listener);
}

/** Deletes the analytics cookies and PostHog's storage keys, on every domain they may sit on. */
export function clearAnalyticsStorage(): void {
  try {
    const host = window.location.hostname;
    for (const part of document.cookie.split(";")) {
      const name = part.trim().split("=")[0];
      if (!name || !ANALYTICS_COOKIE.test(name)) continue;
      for (const domain of cookieDomainsFor(host)) {
        document.cookie = `${name}=; path=/; max-age=0${domain ? `; domain=${domain}` : ""}`;
      }
    }
  } catch {
    // No cookie access: nothing to clear.
  }
  for (const storage of [safeStorage("localStorage"), safeStorage("sessionStorage")]) {
    if (!storage) continue;
    for (const key of Object.keys(storage)) {
      // ph_* / __ph*: PostHog. zettl:first-touch: the campaign that led here (tracking/firstTouch.ts).
      if (key.startsWith("ph_") || key.startsWith("__ph") || key === "zettl:first-touch") {
        storage.removeItem(key);
      }
    }
  }
}

function safeStorage(name: "localStorage" | "sessionStorage"): Storage | null {
  try {
    return window[name];
  } catch {
    return null;
  }
}
